/* 圖譜排版：純函式，不碰 DOM —— 所以它可以在 Node 裡被徹底測試。
   設計約束（來自事實查證）：
   - 手機的 canvas 在 500–1000 個節點還算舒適，2000 以上需要 WebGL。所以有「節點預算」。
   - 超過預算時收斂成「類別聚合球」，而不是硬畫到卡死。
   - 排版必須是確定性的：同樣的輸入畫出同樣的圖，否則每次重畫都在跳動。 */

export interface GraphNode {
  id: string;
  label: string;
  x: number;
  y: number;
  r: number;
  categoryId: string;
  deg: number;
  kind: "concept" | "group";
  count?: number;
}

export interface GraphEdge { from: string; to: string; type: string }

export interface GraphLayout {
  nodes: GraphNode[];
  edges: GraphEdge[];
  /** 是否因為超出預算而收斂成聚合球。 */
  aggregated: boolean;
  /** 因為收斂而沒有單獨畫出來的知識數。 */
  hidden: number;
  iterations: number;
}

export interface LayoutInput {
  concepts: { id: string; summary: string; primaryCategoryId: string }[];
  links: { fromId: string; toId: string; type: string }[];
  categories: { id: string; name: string; seedKey?: string }[];
  width: number;
  height: number;
  /** 一次最多畫幾個節點。手機 800、桌面 2000（實測值見事實查證報告）。 */
  maxNodes?: number;
  iterations?: number;
  seed?: number;
}

/** 確定性的偽隨機：同樣的 seed 一定得到同樣的序列，排版才不會每次重畫都在跳。 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function labelOf(summary: string, max = 12): string {
  const text = summary.trim().replace(/\s+/g, " ");
  return text.length <= max ? text : text.slice(0, max);
}

export function layoutGraph(input: LayoutInput): GraphLayout {
  const maxNodes = input.maxNodes ?? 800;
  const iterations = input.iterations ?? 220;
  const rand = mulberry32(input.seed ?? 20260912);
  const cx = input.width / 2;
  const cy = input.height / 2;

  const degree = new Map<string, number>();
  for (const l of input.links) {
    degree.set(l.fromId, (degree.get(l.fromId) ?? 0) + 1);
    degree.set(l.toId, (degree.get(l.toId) ?? 0) + 1);
  }

  const aggregated = input.concepts.length > maxNodes;
  const nodes: GraphNode[] = [];

  if (aggregated) {
    /* 收斂成類別聚合球：半徑依該類別的知識數，位置固定在圓周上。 */
    const byCategory = new Map<string, string[]>();
    for (const c of input.concepts) {
      const list = byCategory.get(c.primaryCategoryId) ?? [];
      list.push(c.id);
      byCategory.set(c.primaryCategoryId, list);
    }
    const groups = [...byCategory.entries()].sort((a, b) => b[1].length - a[1].length);
    groups.forEach(([categoryId, ids], index) => {
      const angle = (index / Math.max(1, groups.length)) * Math.PI * 2;
      const radius = Math.min(cx, cy) * 0.62;
      nodes.push({
        id: "group:" + categoryId,
        label: input.categories.find((c) => c.id === categoryId)?.name ?? "—",
        x: cx + Math.cos(angle) * radius,
        y: cy + Math.sin(angle) * radius,
        r: 10 + Math.min(22, Math.sqrt(ids.length) * 3),
        categoryId, deg: ids.length, kind: "group", count: ids.length,
      });
    });
    const edges: GraphEdge[] = [];
    const seen = new Set<string>();
    for (const l of input.links) {
      const a = input.concepts.find((c) => c.id === l.fromId);
      const b = input.concepts.find((c) => c.id === l.toId);
      if (!a || !b) continue;
      const key = a.primaryCategoryId + "→" + b.primaryCategoryId;
      if (a.primaryCategoryId === b.primaryCategoryId || seen.has(key)) continue;
      seen.add(key);
      edges.push({ from: "group:" + a.primaryCategoryId, to: "group:" + b.primaryCategoryId, type: l.type });
    }
    return { nodes, edges, aggregated, hidden: input.concepts.length, iterations: 0 };
  }

  /* 個別節點：初始位置放在圓周上（不是隨機散開），再讓力導模擬收斂。 */
  input.concepts.forEach((c, index) => {
    const angle = (index / Math.max(1, input.concepts.length)) * Math.PI * 2 + rand() * 0.2;
    const radius = Math.min(cx, cy) * (0.35 + rand() * 0.4);
    nodes.push({
      id: c.id, label: labelOf(c.summary),
      x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius,
      /* 半徑要看得見：原本 3–4.6px 在手機上像灰塵，整個畫布於是看起來一片空。
         連接數決定大小（星等＝重要度）。 */
      r: 6 + Math.min(10, (degree.get(c.id) ?? 0) * 1.5),
      categoryId: c.primaryCategoryId, deg: degree.get(c.id) ?? 0, kind: "concept",
    });
  });

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const edges: GraphEdge[] = input.links
    .filter((l) => byId.has(l.fromId) && byId.has(l.toId))
    .map((l) => ({ from: l.fromId, to: l.toId, type: l.type }));

  /* 力導：斥力（所有節點互相推開）＋ 彈簧（有連線的拉近）＋ 輕微向心力。 */
  const area = input.width * input.height;
  for (let step = 0; step < iterations; step++) {
    for (const a of nodes) {
      let dx = 0, dy = 0;
      for (const b of nodes) {
        if (a === b) continue;
        const vx = a.x - b.x, vy = a.y - b.y;
        const d2 = vx * vx + vy * vy;
        if (d2 < 1 || d2 > area) continue;
        const force = Math.min(80, (area * 0.0009) / d2);
        dx += (vx / Math.sqrt(d2)) * force;
        dy += (vy / Math.sqrt(d2)) * force;
      }
      a.x += dx * 0.06;
      a.y += dy * 0.06;
    }
    for (const e of edges) {
      const a = byId.get(e.from)!, b = byId.get(e.to)!;
      const vx = b.x - a.x, vy = b.y - a.y;
      const d = Math.max(1, Math.sqrt(vx * vx + vy * vy));
      /* 彈簧長度跟著畫布大小 —— 固定 90 會讓節點全擠在正中央，四周留一大片空白。 */
      const desired = Math.min(input.width, input.height) * 0.3;
      const force = (d - desired) * 0.012;
      a.x += (vx / d) * force;
      a.y += (vy / d) * force;
      b.x -= (vx / d) * force;
      b.y -= (vy / d) * force;
    }
    for (const n of nodes) {
      /* 向心力很輕：只防止飄走，不要把它們吸成一團。 */
      n.x += (cx - n.x) * 0.0012;
      n.y += (cy - n.y) * 0.0012;
      n.x = Math.max(n.r + 2, Math.min(input.width - n.r - 2, n.x));
      n.y = Math.max(n.r + 2, Math.min(input.height - n.r - 2, n.y));
    }
  }

  fitToCanvas(nodes, input.width, input.height);
  return { nodes, edges, aggregated: false, hidden: 0, iterations };
}

/** 貼合畫布：力導會讓小圖縮成一團，這裡把整張圖放大到填滿可用空間。
    只放大不縮小，而且半徑不變 —— 否則節點會大到互相重疊。 */
function fitToCanvas(nodes: GraphNode[], width: number, height: number, pad = 28, maxScale = 2.6): void {
  if (nodes.length < 2) return;
  const minX = Math.min(...nodes.map((n) => n.x));
  const maxX = Math.max(...nodes.map((n) => n.x));
  const minY = Math.min(...nodes.map((n) => n.y));
  const maxY = Math.max(...nodes.map((n) => n.y));
  const spanX = Math.max(1, maxX - minX);
  const spanY = Math.max(1, maxY - minY);
  const scale = Math.min((width - pad * 2) / spanX, (height - pad * 2) / spanY, maxScale);
  if (!Number.isFinite(scale) || scale <= 1.05) return;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  for (const n of nodes) {
    n.x = width / 2 + (n.x - cx) * scale;
    n.y = height / 2 + (n.y - cy) * scale;
    n.x = Math.max(n.r + 2, Math.min(width - n.r - 2, n.x));
    n.y = Math.max(n.r + 2, Math.min(height - n.r - 2, n.y));
  }
}

/** 標籤什麼時候該消失：縮得太小就別畫字 —— 這是手機上最貴的東西。 */
export function labelsVisibleAt(scale: number): boolean {
  return scale >= 0.75;
}

/** 兩個節點之間的距離，供點擊命中測試。 */
export function hitTest(layout: GraphLayout, x: number, y: number, slack = 6): GraphNode | null {
  let best: GraphNode | null = null;
  let bestDist = Infinity;
  for (const n of layout.nodes) {
    const d = Math.hypot(n.x - x, n.y - y);
    if (d <= n.r + slack && d < bestDist) { best = n; bestDist = d; }
  }
  return best;
}
